import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, 
  Target,
  Handshake,
  TrendingUp,
  ChevronDown,
  Rocket,
  Star,
  Sparkles
} from "lucide-react";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LandingPage = () => {
  const [stats, setStats] = useState({ coaches: 0, gyms: 0, clients: 0, applications: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/stats`);
        setStats(response.data);
      } catch (err) {
        console.error("Failed to fetch stats:", err);
      }
    };
    fetchStats();
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const faqItems = [
    {
      id: "faq-1",
      question: "Quels diplômes sont requis pour rejoindre APM ?",
      answer: "Pour rejoindre APM, vous devez être titulaire d'un diplôme reconnu dans le domaine du sport : BPJEPS, DEJEPS, CQP Instructeur Fitness, ou tout autre diplôme équivalent permettant l'encadrement sportif contre rémunération."
    },
    {
      id: "faq-2",
      question: "Comment fonctionne le partenariat avec Basic-Fit ?",
      answer: "APM a établi des partenariats avec 3 salles Basic-Fit : Manosque, Gignac-la-Nerthe et Martigues. Une fois affilié, vous avez accès à ces salles pour exercer votre activité de coach indépendant."
    },
    {
      id: "faq-3",
      question: "Quel est l'objectif de revenus ?",
      answer: "Notre objectif est de vous accompagner pour atteindre 3000€/mois minimum en 90 jours. Grâce à notre méthode éprouvée et notre accompagnement personnalisé, vous construisez une clientèle fidèle rapidement."
    },
    {
      id: "faq-4",
      question: "Est-ce que je garde mon indépendance ?",
      answer: "Absolument ! Vous restez totalement indépendant. APM vous apporte les outils, la formation et l'accès aux salles, mais vous gérez votre activité comme vous le souhaitez."
    },
    {
      id: "faq-5",
      question: "Pourquoi rejoindre APM maintenant ?",
      answer: "APM est un projet ambitieux lancé en 2026. En nous rejoignant dès le départ, vous faites partie des pionniers et bénéficiez d'un accompagnement personnalisé et privilégié pour construire votre réussite."
    }
  ];

  return (
    <div className="min-h-screen bg-[#05050A]">
      <Header />

      {/* Hero Section */}
      <section className="hero-section relative" data-testid="hero-section">
        <div 
          className="hero-bg"
          style={{
            backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/00dc70e4-70e9-471a-a527-cf5ef1f5c6b5/images/a886499da1d47e516003695ffbdd315ce1b8c7259cf27df693d6afd774d0997d.png')`
          }}
        />
        <div className="hero-overlay" />
        
        <div className="container-custom relative z-10 pt-32">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <Sparkles className="w-5 h-5 text-[#0066FF]" />
              <span className="text-[#0066FF] text-sm font-bold uppercase tracking-[0.2em]">
                Nouveau en 2026
              </span>
            </div>
            
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter leading-[0.95] mb-8">
              Devenez coach<br />
              <span className="text-[#0066FF]">indépendant</span><br />
              et atteignez 3000€/mois
            </h1>
            
            <p className="text-[#94A3B8] text-lg md:text-xl max-w-xl mb-10 leading-relaxed">
              90 jours pour lancer et stabiliser votre activité de personal trainer 
              avec un accompagnement personnalisé et un accès aux salles Basic-Fit.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/apply"
                className="btn-primary flex items-center justify-center gap-2"
                data-testid="hero-cta-apply"
              >
                Rejoindre l&apos;aventure
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/coaches"
                className="px-8 py-4 border border-white/20 text-white font-bold uppercase tracking-wider hover:bg-white/5 transition-all text-center"
                data-testid="hero-cta-coaches"
              >
                Nos Coachs
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ChevronDown className="w-8 h-8 text-white/50" />
        </motion.div>
      </section>

      {/* Stats Section - Objectifs & Ambition */}
      <section className="section-padding relative" data-testid="stats-section">
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/00dc70e4-70e9-471a-a527-cf5ef1f5c6b5/images/f2cd200b3db4fc136cc5daabd7828ff2fb8403cc5bd88569b0aeef2fd67a6f97.png')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="container-custom relative z-10">
          <motion.div 
            className="text-center mb-12"
            {...fadeInUp}
            viewport={{ once: true }}
          >
            <span className="text-[#0066FF] text-sm font-bold uppercase tracking-[0.2em] mb-4 block">
              Notre ambition
            </span>
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight">
              Les objectifs APM
            </h2>
          </motion.div>
          <motion.div 
            className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <motion.div className="stat-card" variants={fadeInUp} data-testid="stat-objective">
              <div className="stat-number">3000€</div>
              <div className="stat-label">Objectif / Mois</div>
            </motion.div>
            <motion.div className="stat-card" variants={fadeInUp} data-testid="stat-days">
              <div className="stat-number">90</div>
              <div className="stat-label">Jours d&apos;accompagnement</div>
            </motion.div>
            <motion.div className="stat-card" variants={fadeInUp} data-testid="stat-gyms">
              <div className="stat-number">3</div>
              <div className="stat-label">Salles Partenaires</div>
            </motion.div>
            <motion.div className="stat-card" variants={fadeInUp} data-testid="stat-year">
              <div className="stat-number">5</div>
              <div className="stat-label">Objectif 2027</div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section - POSTULEZ MAINTENANT (moved up) */}
      <section className="section-padding bg-[#0A1628]" data-testid="cta-section">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-2xl mx-auto"
            {...fadeInUp}
            viewport={{ once: true }}
          >
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight mb-6 text-white">
              Prêt à transformer<br />votre carrière ?
            </h2>
            <p className="text-white/70 mb-10 leading-relaxed">
              Rejoignez APM et commencez votre parcours vers l&apos;indépendance 
              en tant que coach sportif. Places limitées !
            </p>
            <Link
              to="/apply"
              className="bg-[#0066FF] hover:bg-[#3B82F6] text-white font-bold px-8 py-4 uppercase tracking-wider inline-flex items-center gap-2 transition-all"
              data-testid="cta-apply-button"
            >
              Postuler maintenant
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Why APM Section */}
      <section className="section-padding bg-[#0F0F16]" data-testid="why-section">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            {...fadeInUp}
            viewport={{ once: true }}
          >
            <span className="text-[#0066FF] text-sm font-bold uppercase tracking-[0.2em] mb-4 block">
              Pourquoi APM ?
            </span>
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight mb-6">
              La méthode pour réussir<br />en tant que coach indépendant
            </h2>
            <p className="text-[#94A3B8] leading-relaxed">
              Vivre de sa passion est possible, à condition d&apos;adopter une méthode structurée 
              pour attirer et fidéliser des clients motivés.
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <motion.div className="method-step" variants={fadeInUp}>
              <div className="method-icon">
                <Handshake className="w-10 h-10 text-[#0066FF]" />
              </div>
              <h3 className="font-['Outfit'] text-xl font-bold uppercase mb-4">Rencontrer</h3>
              <p className="text-[#94A3B8] leading-relaxed">
                Nous vous formons à la prise de contact en douceur. Une approche respectueuse 
                pour mettre à l&apos;aise l&apos;adhérent et le coach.
              </p>
            </motion.div>

            <motion.div className="method-step" variants={fadeInUp}>
              <div className="method-icon">
                <Target className="w-10 h-10 text-[#0066FF]" />
              </div>
              <h3 className="font-['Outfit'] text-xl font-bold uppercase mb-4">Signer</h3>
              <p className="text-[#94A3B8] leading-relaxed">
                Apprenez à gérer un rendez-vous de vente, de l&apos;étude des besoins à la 
                présentation des services.
              </p>
            </motion.div>

            <motion.div className="method-step" variants={fadeInUp}>
              <div className="method-icon">
                <TrendingUp className="w-10 h-10 text-[#0066FF]" />
              </div>
              <h3 className="font-['Outfit'] text-xl font-bold uppercase mb-4">Fidéliser</h3>
              <p className="text-[#94A3B8] leading-relaxed">
                Faites vivre une expérience extraordinaire à vos clients pour les fidéliser 
                sur le long terme.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Founder Section - Florian GLEIZE */}
      <section className="section-padding" data-testid="founder-section">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            {...fadeInUp}
            viewport={{ once: true }}
          >
            <span className="text-[#0066FF] text-sm font-bold uppercase tracking-[0.2em] mb-4 block">
              Le Fondateur
            </span>
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight">
              Votre partenaire vers la réussite
            </h2>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
            {...fadeInUp}
            viewport={{ once: true }}
          >
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_coach-hub-96/artifacts/dlu0bgyt_DSC05812.jpg" 
                alt="Florian GLEIZE - Fondateur APM"
                className="w-full max-w-md mx-auto object-cover"
                data-testid="founder-image"
              />
              <div className="absolute -bottom-4 -right-4 bg-[#0066FF] px-6 py-3">
                <span className="font-['Outfit'] font-bold uppercase text-sm">Fondateur APM</span>
              </div>
            </div>
            
            <div>
              <h3 className="font-['Outfit'] text-3xl font-bold mb-4">
                Florian <span className="text-[#0066FF]">GLEIZE</span>
              </h3>
              <p className="text-[#94A3B8] leading-relaxed mb-6">
                Coach passionné avec <strong className="text-white">4 ans d&apos;expérience en tant qu&apos;entrepreneur</strong>, 
                j&apos;ai traversé toutes les difficultés que connaissent les coachs : 
                <span className="text-[#0066FF]"> visibilité, légitimité, fidélisation, conversion des prospects...</span>
              </p>
              <p className="text-[#94A3B8] leading-relaxed mb-6">
                Aujourd&apos;hui, <strong className="text-white">fort de mon expérience et après m&apos;être formé</strong>, 
                j&apos;ai créé APM pour accompagner les coachs dans leur réussite. Mon objectif : vous transmettre 
                les clés qui m&apos;ont permis de réussir et vous aider à atteindre <span className="text-[#0066FF]">3000€/mois en 90 jours</span>.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 text-sm">
                  <Star className="w-5 h-5 text-[#0066FF]" />
                  <span>4 ans d&apos;entrepreneuriat</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Rocket className="w-5 h-5 text-[#0066FF]" />
                  <span>Méthode éprouvée</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Target className="w-5 h-5 text-[#0066FF]" />
                  <span>Accompagnement personnalisé</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section - Moved to bottom */}
      <section className="section-padding bg-[#0F0F16]" data-testid="faq-section">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            {...fadeInUp}
            viewport={{ once: true }}
          >
            <span className="text-[#0066FF] text-sm font-bold uppercase tracking-[0.2em] mb-4 block">
              FAQ
            </span>
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight">
              Questions fréquentes
            </h2>
          </motion.div>

          <motion.div 
            className="max-w-3xl mx-auto"
            {...fadeInUp}
            viewport={{ once: true }}
          >
            <Accordion type="single" collapsible className="space-y-2">
              {faqItems.map((item) => (
                <AccordionItem 
                  key={item.id} 
                  value={item.id}
                  className="border border-white/10 bg-[#05050A] px-6"
                  data-testid={`faq-item-${item.id}`}
                >
                  <AccordionTrigger className="text-left font-semibold py-5 hover:text-[#0066FF] hover:no-underline">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#94A3B8] pb-5 leading-relaxed">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
